import type { PrimeOMSParams } from '@src/components/OMS/NewOrder/types';
import { OMSView } from '@src/components/OMS/OMSView';
import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import {
  allowContracts,
  MixpanelEvent,
  MixpanelEventProperty,
  PricingMode,
  SideEnum,
  useDynamicCallback,
  useMixpanel,
  useSecuritiesContext,
} from '@talos/kyoko';
import type { CellClickedEvent } from 'ag-grid-community';
import Big from 'big.js';
import { orderSliceActions } from '../../../../../components/OMS/NewOrder/OrderSlice';
import { openView } from '../../../../../components/OMS/OMSSlice';
import { getNormalizedOrderQty, useGetOrderCurrency } from '../../../../../components/OMS/useGetOrderCurrency';
import { useTradingSettings } from '../../../../../providers/TradingSettingsContext';
import { selectOptionFilter } from '../Filter/selectors';
import {
  OPTION_BID_IV,
  OPTION_BID_PRICE,
  OPTION_BID_SIZE,
  OPTION_OFFER_IV,
  OPTION_OFFER_PRICE,
  OPTION_OFFER_SIZE,
  OPTION_POSITION,
  OPTION_STRIKE_PRICE,
  type OptionChainDataRow,
} from '../types';
import { isOptionTypeEnum } from '../utils';

/**
 * Returns function which primes OMS.
 * https://talostrading.atlassian.net/browse/UI-2826
 */
export function useOptionChainPrimeOms({ panelID }: { panelID: string }) {
  const { securitiesBySymbol } = useSecuritiesContext();
  const optionFilter = useAppStateSelector(state => selectOptionFilter(state, panelID));
  const dispatch = useAppStateDispatch();
  const { enableDerivativeContractDefault } = useTradingSettings();
  const { getOrderCurrency } = useGetOrderCurrency();

  const mixpanel = useMixpanel();

  return useDynamicCallback(({ colDef, data, value }: CellClickedEvent<OptionChainDataRow>) => {
    const fieldPath = colDef?.field;
    if (fieldPath == null || !data) {
      throw new Error('Missing field in column definition');
    }

    const [optionType, field] = fieldPath.split('.');

    // If the optionType is wrong or there is no field, do nothing
    if (!isOptionTypeEnum(optionType) || !field) {
      return;
    }

    const symbol = data[optionType]?.Symbol;
    const security = symbol ? securitiesBySymbol.get(symbol) : undefined;

    if (!symbol || !security) {
      return;
    }

    let limitPx: string | undefined;
    let intent: SideEnum = SideEnum.Buy;

    let pricingMode: PricingMode = PricingMode.Default;
    const orderCurrency = enableDerivativeContractDefault && allowContracts(security) ? '' : getOrderCurrency(security);
    let qty = getNormalizedOrderQty({
      security,
      orderCurrency,
    });
    switch (field) {
      case OPTION_OFFER_PRICE: {
        intent = SideEnum.Buy;
        limitPx = value;
        break;
      }
      case OPTION_OFFER_IV: {
        if (security?.SupportedPricingModes?.ImpliedVolatility && data[optionType]?.BestOfferIV) {
          intent = SideEnum.Buy;
          limitPx = value;
          pricingMode = PricingMode.ImpliedVolatility;
        }
        break;
      }
      case OPTION_OFFER_SIZE: {
        intent = SideEnum.Buy;
        limitPx = data[optionType]?.OfferPrice;
        qty = value;
        break;
      }
      case OPTION_BID_PRICE: {
        intent = SideEnum.Sell;
        limitPx = value;
        break;
      }
      case OPTION_BID_IV: {
        if (security?.SupportedPricingModes?.ImpliedVolatility && data[optionType]?.BestBidIV) {
          intent = SideEnum.Sell;
          limitPx = value;
          pricingMode = PricingMode.ImpliedVolatility;
        }
        break;
      }
      case OPTION_BID_SIZE: {
        intent = SideEnum.Sell;
        limitPx = data[optionType]?.BidPrice;
        qty = value;
        break;
      }
      case OPTION_POSITION: {
        const position = data[optionType]?.Position;
        if (!position && position !== '') {
          intent = SideEnum.Buy;
          break;
        }
        const positionIsPositive = Big(position).gt(0);

        intent = positionIsPositive ? SideEnum.Sell : SideEnum.Buy;
        limitPx = positionIsPositive ? data[optionType]?.BidPrice : data[optionType]?.OfferPrice;
        qty = Big(position).abs().toFixed();

        break;
      }
      case OPTION_STRIKE_PRICE: {
        // There is no way to determine which symbol they wish to
        // prime the order with, so do nothing
        return;
      }
      default: {
        intent = SideEnum.Buy;
      }
    }

    mixpanel.track(MixpanelEvent.PrimeOrder, {
      [MixpanelEventProperty.Column]: field,
      [MixpanelEventProperty.Type]: optionType,
    });

    const draftedOrder: PrimeOMSParams = {
      side: intent,
      orderQty: qty,
      price: limitPx ? Big(limitPx).toFixed() : undefined,
      symbol: symbol,
      marketAccounts: optionFilter.marketAccountName ? [optionFilter.marketAccountName] : [],
      currency: orderCurrency,
      pricingMode,
    };

    dispatch(openView(OMSView.NewOrderForm));
    dispatch(orderSliceActions.primeNewOrderForm(draftedOrder));
  });
}
